import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getOrders, getStats, updateOrderStatus, getMe, fileUrl, getProducts, generateCopy } from "@/lib/api";
import { LogOut, Package, Sparkles, Euro, ExternalLink, X, Wand2, Copy, Check } from "lucide-react";
import { toast } from "sonner";

const STATUSES = ["nouveau", "en cours", "devis envoyé", "confirmé", "terminé"];
const STATUS_COLOR = {
  nouveau: "bg-[#0044ff] text-white",
  "en cours": "bg-amber-400 text-black",
  "devis envoyé": "bg-purple-500 text-white",
  confirmé: "bg-emerald-500 text-white",
  terminé: "bg-neutral-700 text-white",
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState({ orders: 0, new: 0, value: 0 });
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);

  // AI copy generator
  const [products, setProducts] = useState([]);
  const [genMode, setGenMode] = useState("catalog"); // catalog | prompt
  const [genSlug, setGenSlug] = useState("");
  const [genPrompt, setGenPrompt] = useState("");
  const [genLoading, setGenLoading] = useState(false);
  const [genResult, setGenResult] = useState(null);
  const [copied, setCopied] = useState("");

  const load = () => {
    Promise.all([getOrders(), getStats()])
      .then(([o, s]) => {
        setOrders(o);
        setStats(s);
      })
      .catch(() => navigate("/admin/login"))
      .finally(() => setLoading(false));
    getProducts().then((p) => { setProducts(p); if (p[0]) setGenSlug(p[0].slug); }).catch(() => {});
  };

  useEffect(() => {
    if (!localStorage.getItem("ms_token")) {
      navigate("/admin/login");
      return;
    }
    getMe().then(load).catch(() => navigate("/admin/login"));
  }, [navigate]);

  const changeStatus = async (id, status) => {
    setOrders((prev) => prev.map((o) => (o.id === id ? { ...o, status } : o)));
    if (selected?.id === id) setSelected({ ...selected, status });
    await updateOrderStatus(id, status).catch(() => {});
    getStats().then(setStats).catch(() => {});
  };

  const logout = () => {
    localStorage.removeItem("ms_token");
    navigate("/admin/login");
  };

  const runGenerate = async () => {
    if (genLoading) return;
    if (genMode === "prompt" && !genPrompt.trim()) {
      toast.error("Décris le produit à générer.");
      return;
    }
    setGenLoading(true);
    setGenResult(null);
    try {
      const payload = genMode === "catalog" ? { slug: genSlug } : { prompt: genPrompt };
      const res = await generateCopy(payload);
      setGenResult(res);
    } catch {
      toast.error("La génération a échoué. Réessayez.");
    } finally {
      setGenLoading(false);
    }
  };

  const copyText = (key, text) => {
    navigator.clipboard.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(""), 1500);
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center font-mono text-sm">Chargement…</div>;

  const StatCard = ({ icon: Icon, label, value }) => (
    <div className="border border-[#e5e5e5] bg-white p-6">
      <Icon className="w-5 h-5 text-[#0044ff]" />
      <p className="font-display font-black text-4xl tracking-tighter mt-4">{value}</p>
      <p className="font-mono text-xs uppercase tracking-widest text-[#555] mt-1">{label}</p>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#f9f9f8]">
      <header className="bg-[#0a0a0a] text-white">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 h-16 flex items-center justify-between">
          <span className="font-display font-black uppercase tracking-tight">MerchStudio · Admin</span>
          <button onClick={logout} data-testid="admin-logout" className="flex items-center gap-2 font-mono text-xs uppercase tracking-widest hover:text-[#0044ff]">
            <LogOut className="w-4 h-4" /> Déconnexion
          </button>
        </div>
      </header>

      <main className="max-w-[1600px] mx-auto px-6 md:px-12 py-10">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10">
          <StatCard icon={Package} label="Demandes totales" value={stats.orders} />
          <StatCard icon={Sparkles} label="Nouvelles" value={stats.new} />
          <StatCard icon={Euro} label="Valeur estimée" value={`${Math.round(stats.value).toLocaleString("fr-FR")}€`} />
        </div>

        {/* AI copy generator */}
        <div className="border border-[#e5e5e5] bg-white p-6 mb-10" data-testid="ai-generator">
          <div className="flex items-center gap-2 mb-1">
            <Wand2 className="w-5 h-5 text-[#0044ff]" />
            <h2 className="font-display font-black text-xl tracking-tight uppercase">Générateur&nbsp;IA — Accroche &amp; description</h2>
          </div>
          <p className="font-mono text-xs uppercase tracking-widest text-[#555] mb-5">Propulsé par OpenAI — nom d'accroche + description produit</p>

          <div className="flex gap-2 mb-4">
            <button
              data-testid="gen-mode-catalog"
              onClick={() => setGenMode("catalog")}
              className={`font-mono text-xs uppercase tracking-widest px-4 py-2 border transition-colors ${genMode === "catalog" ? "bg-[#0a0a0a] text-white border-[#0a0a0a]" : "bg-white text-[#555] border-[#e5e5e5] hover:border-[#0a0a0a]"}`}
            >
              Produit du catalogue
            </button>
            <button
              data-testid="gen-mode-prompt"
              onClick={() => setGenMode("prompt")}
              className={`font-mono text-xs uppercase tracking-widest px-4 py-2 border transition-colors ${genMode === "prompt" ? "bg-[#0a0a0a] text-white border-[#0a0a0a]" : "bg-white text-[#555] border-[#e5e5e5] hover:border-[#0a0a0a]"}`}
            >
              Brief libre
            </button>
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            {genMode === "catalog" ? (
              <select
                data-testid="gen-product-select"
                value={genSlug}
                onChange={(e) => setGenSlug(e.target.value)}
                className="flex-1 border border-[#e5e5e5] px-4 py-3 text-sm outline-none focus:border-[#0044ff] bg-white"
              >
                {products.map((p) => <option key={p.slug} value={p.slug}>{p.name}</option>)}
              </select>
            ) : (
              <input
                data-testid="gen-prompt-input"
                value={genPrompt}
                onChange={(e) => setGenPrompt(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && runGenerate()}
                placeholder="Ex : casquette bio en lin, coupe trucker, marquage brodé…"
                className="flex-1 border border-[#e5e5e5] px-4 py-3 text-sm outline-none focus:border-[#0044ff]"
              />
            )}
            <button
              data-testid="gen-run-btn"
              onClick={runGenerate}
              disabled={genLoading}
              className="flex items-center justify-center gap-2 bg-[#0044ff] text-white px-6 py-3 font-mono text-xs uppercase tracking-widest hover:bg-[#0033cc] transition-colors disabled:opacity-50"
            >
              <Wand2 className="w-4 h-4" /> {genLoading ? "Génération…" : "Générer"}
            </button>
          </div>

          {genResult && (
            <div className="mt-6 space-y-4" data-testid="gen-result">
              <div className="border border-[#e5e5e5] p-4">
                <div className="flex items-center justify-between mb-1">
                  <p className="font-mono text-[10px] uppercase tracking-widest text-[#999]">Accroche</p>
                  <button onClick={() => copyText("tagline", genResult.tagline)} className="text-[#0044ff] hover:text-[#0033cc]" data-testid="copy-tagline">
                    {copied === "tagline" ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </button>
                </div>
                <p className="font-display font-bold text-lg">{genResult.tagline || "—"}</p>
              </div>
              <div className="border border-[#e5e5e5] p-4">
                <div className="flex items-center justify-between mb-1">
                  <p className="font-mono text-[10px] uppercase tracking-widest text-[#999]">Description</p>
                  <button onClick={() => copyText("description", genResult.description)} className="text-[#0044ff] hover:text-[#0033cc]" data-testid="copy-description">
                    {copied === "description" ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </button>
                </div>
                <p className="text-sm leading-relaxed">{genResult.description || genResult.raw}</p>
              </div>
            </div>
          )}
        </div>

        <div className="border border-[#e5e5e5] bg-white overflow-x-auto">
          <table className="w-full text-sm" data-testid="orders-table">
            <thead>
              <tr className="border-b border-[#e5e5e5] font-mono text-xs uppercase tracking-widest text-[#555]">
                <th className="text-left p-4">Client</th>
                <th className="text-left p-4 hidden md:table-cell">Produits</th>
                <th className="text-right p-4">Total</th>
                <th className="text-left p-4">Statut</th>
                <th className="text-left p-4"></th>
              </tr>
            </thead>
            <tbody>
              {orders.length === 0 && (
                <tr><td colSpan={5} className="p-10 text-center text-[#555]">Aucune demande pour l'instant.</td></tr>
              )}
              {orders.map((o) => (
                <tr key={o.id} data-testid={`order-row-${o.id}`} className="border-b border-[#f0f0f0] hover:bg-[#f9f9f8]">
                  <td className="p-4">
                    <p className="font-bold">{o.customer.name}</p>
                    <p className="text-[#555] text-xs">{o.customer.company || o.customer.email}</p>
                  </td>
                  <td className="p-4 hidden md:table-cell text-[#555]">{o.items.map((i) => `${i.quantity}× ${i.name}${i.color ? ` (${i.color})` : ""}`).join(", ")}</td>
                  <td className="p-4 text-right font-display font-bold">{Math.round(o.total).toLocaleString("fr-FR")}€</td>
                  <td className="p-4">
                    <select
                      data-testid={`status-select-${o.id}`}
                      value={o.status}
                      onChange={(e) => changeStatus(o.id, e.target.value)}
                      className={`text-xs font-bold uppercase tracking-wide px-2 py-1 outline-none cursor-pointer ${STATUS_COLOR[o.status] || "bg-neutral-200"}`}
                    >
                      {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </td>
                  <td className="p-4">
                    <button onClick={() => setSelected(o)} data-testid={`view-order-${o.id}`} className="font-mono text-xs uppercase tracking-widest text-[#0044ff] hover:underline">
                      Détails
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </main>

      {/* Detail drawer */}
      {selected && (
        <div className="fixed inset-0 z-50 flex justify-end" onClick={() => setSelected(null)}>
          <div className="absolute inset-0 bg-black/40" />
          <div onClick={(e) => e.stopPropagation()} data-testid="order-detail" className="relative w-full max-w-md bg-white h-full overflow-y-auto p-8">
            <button onClick={() => setSelected(null)} className="absolute top-6 right-6 text-[#555] hover:text-black"><X className="w-5 h-5" /></button>
            <p className="font-mono text-xs uppercase tracking-widest text-[#555]">Réf. {selected.id.slice(0, 8)}</p>
            <h2 className="font-display font-black text-3xl tracking-tighter mt-2">{selected.customer.name}</h2>
            <div className="mt-4 space-y-1 text-sm text-[#555]">
              {selected.customer.company && <p>{selected.customer.company}</p>}
              <p>{selected.customer.email}</p>
              {selected.customer.phone && <p>{selected.customer.phone}</p>}
            </div>

            <div className="mt-8 border-t border-[#e5e5e5] pt-6 space-y-3">
              {selected.items.map((i) => (
                <div key={`${i.slug}-${i.color || ""}`} className="flex justify-between text-sm">
                  <span>{i.quantity} × {i.name}{i.color ? ` · ${i.color}` : ""} <span className="text-[#999]">({i.unit_price}€)</span></span>
                  <span className="font-bold">{Math.round(i.line_total).toLocaleString("fr-FR")}€</span>
                </div>
              ))}
              <div className="flex justify-between font-display font-bold text-xl border-t border-[#e5e5e5] pt-3">
                <span>Total</span><span>{Math.round(selected.total).toLocaleString("fr-FR")}€</span>
              </div>
            </div>

            {selected.message && (
              <div className="mt-6"><p className="font-mono text-xs uppercase tracking-widest text-[#555] mb-2">Message</p><p className="text-sm">{selected.message}</p></div>
            )}

            {selected.items.some((i) => i.preview_front || i.preview_back) && (
              <div className="mt-6" data-testid="order-previews">
                <p className="font-mono text-xs uppercase tracking-widest text-[#555] mb-3">Aperçu personnalisé (à reproduire)</p>
                <div className="space-y-4">
                  {selected.items.map((i) => {
                    if (!i.preview_front && !i.preview_back) return null;
                    return (
                      <div key={`${i.slug}-${i.color || ""}`} className="border border-[#e5e5e5] p-3">
                        <p className="font-mono text-[10px] uppercase tracking-widest text-[#999] mb-2">{i.name}{i.color ? ` · ${i.color}` : ""}</p>
                        <div className="grid grid-cols-2 gap-2">
                          {["front", "back"].map((sd) => {
                            const url = i[`preview_${sd}`];
                            if (!url) return null;
                            return (
                              <a key={sd} href={fileUrl(url)} target="_blank" rel="noreferrer" data-testid={`preview-${i.slug}-${sd}`} className="block group">
                                <img src={fileUrl(url)} alt={`Aperçu ${sd}`} className="w-full border border-[#e5e5e5] bg-white group-hover:border-[#0044ff] transition-colors" />
                                <span className="block text-center font-mono text-[9px] uppercase tracking-widest text-[#999] mt-1">{sd === "front" ? "Devant" : "Dos"}</span>
                              </a>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {selected.customization && (
              <div className="mt-6 space-y-4">
                <p className="font-mono text-xs uppercase tracking-widest text-[#555]">Personnalisation</p>
                {["front", "back"].map((sd) => {
                  const cz = selected.customization[sd] || {};
                  if (!cz.logo_url && !cz.text) return null;
                  return (
                    <div key={sd} className="border border-[#e5e5e5] p-3">
                      <p className="font-mono text-[10px] uppercase tracking-widest text-[#999] mb-2">{sd === "front" ? "Devant" : "Dos"}</p>
                      {cz.text && <p className="text-sm mb-2">Texte : <b>« {cz.text} »</b></p>}
                      {cz.logo_url && (
                        <a href={fileUrl(cz.logo_url)} target="_blank" rel="noreferrer" data-testid={`order-logo-${sd}`} className="inline-flex items-center gap-2 bg-black text-white px-4 py-2 font-bold uppercase tracking-widest text-[11px] hover:bg-[#0044ff] transition-colors">
                          <ExternalLink className="w-3.5 h-3.5" /> Logo {sd === "front" ? "devant" : "dos"}
                        </a>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
